import i18n from '@/utils/i18n';
import React, { useEffect, useState } from 'react';
import { AgentThreadRowActions } from '@/experiences/sales-portal/domain/superintelligence/components/agent-thread-row-actions';
import {
  Badge,
  Button,
  EmptyState,
  Icons,
  List,
  SearchInput,
  Skeleton,
  Spinner,
  Typography,
  XStack,
} from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import { formatShortDate, formatTime } from '@/utils/date';
import { isWeb } from '@/utils/navigation';
import {
  matchesThreadSearch,
  ThreadDateGroup,
  threadDateGroup,
  ThreadRunState,
  threadRunState,
} from '@/experiences/sales-portal/domain/superintelligence/blocs/utils/thread-list-groups';
import { readThreadItemDetails } from '@/experiences/sales-portal/domain/superintelligence/blocs/utils/thread-item-details';
import { useAgUiInterrupts } from '@assistant-ui/react-ag-ui';
import { useAui, useAuiState } from '@assistant-ui/store';
import { useOpenThread } from '@/experiences/sales-portal/domain/superintelligence/blocs/hooks/use-open-thread';
import { useSuperintelligenceContext } from '@/experiences/sales-portal/domain/superintelligence/containers/superintelligence-runtime-provider';
import { useThreadListQuery } from '@/experiences/sales-portal/domain/superintelligence/blocs/queries/use-thread-list-query';
import { View } from 'react-native';

const SKELETON_ROWS = 4;
const SKELETON_ROW_HEIGHT = 36;
const SKELETON_RADIUS = 4;

const GROUP_ORDER: ThreadDateGroup[] = [
  'today',
  'yesterday',
  'previous_7_days',
  'older',
];

interface ThreadRow {
  id: string;
  persisted: boolean;
  runState: ThreadRunState | null;
  savedTitle: string | null;
  title: string;
  updatedAt?: string;
}

const formatUpdatedAt = (iso: string) => {
  const date = new Date(iso);
  return threadDateGroup(iso) === 'today'
    ? formatTime(date)
    : formatShortDate(date);
};

const PINNED_REVEAL = {
  alignItems: 'stretch',
  alignSelf: 'stretch',
  height: 'auto',
  marginLeft: 0,
  opacity: 1,
  overflow: 'visible',
  width: 'auto',
} as const;

interface ThreadRowItemProps {
  archived: boolean;
  onPress: () => void;
  row: ThreadRow;
  selected: boolean;
}

const ThreadRowItem: React.FC<ThreadRowItemProps> = ({
  archived,
  onPress,
  row,
  selected,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const actions = row.persisted ? (
    <AgentThreadRowActions
      archived={archived}
      inRow
      onOpenChange={setMenuOpen}
      threadId={row.id}
      title={row.savedTitle}
    />
  ) : null;

  return (
    <List.Item
      aria-current={selected ? 'true' : undefined}
      onPress={onPress}
      pressable
      selected={selected}
      size="$sm"
    >
      <List.Item.Content>
        <List.Item.Title numberOfLines={1}>{row.title}</List.Item.Title>
      </List.Item.Content>
      <List.Item.Trailing>
        <XStack alignItems="center" gap="$8">
          {row.runState === 'waiting' ? (
            <Badge color="warning" size="$sm">
              {i18n.t('sales_portal.superintelligence.status_waiting')}
            </Badge>
          ) : null}
          {row.runState === 'running' ? (
            <View
              accessibilityLabel={i18n.t(
                'sales_portal.superintelligence.status_running',
              )}
              accessible
            >
              <Spinner size="small" />
            </View>
          ) : null}
          {row.runState === null && row.updatedAt ? (
            <Typography color="$textTertiary" size="$xs">
              {formatUpdatedAt(row.updatedAt)}
            </Typography>
          ) : null}
          {isWeb ? null : actions}
        </XStack>
      </List.Item.Trailing>
      {isWeb && actions ? (
        <List.Item.Trailing revealable {...(menuOpen && PINNED_REVEAL)}>
          {actions}
        </List.Item.Trailing>
      ) : null}
    </List.Item>
  );
};

interface AgentThreadListProps {
  onSelect?: () => void;
}

export const AgentThreadList: React.FC<AgentThreadListProps> = ({
  onSelect,
}) => {
  const [styles] = useStyles();
  const aui = useAui();
  const [query, setQuery] = useState('');
  const mainThreadId = useAuiState((s) => s.threads.mainThreadId);
  const regularIds = useAuiState((s) => s.threads.threadIds);
  const archivedIds = useAuiState((s) => s.threads.archivedThreadIds);
  const threadItems = useAuiState((s) => s.threads.threadItems);
  const isRunning = useAuiState((s) => s.thread.isRunning);
  const awaitingInput = useAgUiInterrupts().length > 0;
  const { runInFlight } = useSuperintelligenceContext();
  const { archived: openThreadArchived } = useOpenThread();
  const [showArchived, setShowArchived] = useState(openThreadArchived);
  useEffect(() => {
    setShowArchived(openThreadArchived);
  }, [mainThreadId, openThreadArchived]);
  const regularThreads = useThreadListQuery('regular');
  const archivedThreads = useThreadListQuery('archived', showArchived);
  const list = showArchived ? archivedThreads : regularThreads;
  const ids = showArchived ? archivedIds : regularIds;
  const newConversation = i18n.t(
    'sales_portal.superintelligence.new_conversation',
  );

  const mainRunState = (): ThreadRunState | null => {
    if (awaitingInput) {
      return 'waiting';
    }
    return isRunning || runInFlight ? 'running' : null;
  };

  const rows = ids
    .map((id): ThreadRow => {
      const item = threadItems.find((thread) => thread.id === id);
      const details = readThreadItemDetails(item?.custom);
      return {
        id,
        persisted: details.persisted,
        runState:
          id === mainThreadId ? mainRunState() : threadRunState(details),
        savedTitle: item?.title ?? null,
        title: item?.title ?? newConversation,
        updatedAt: details.updatedAt,
      };
    })
    .filter((row) => matchesThreadSearch(row.title, query));

  const select = (id: string) => {
    aui.threads.switchToThread(id);
    onSelect?.();
  };

  const renderBody = () => {
    if (list.isLoading) {
      return (
        <View style={styles.skeleton}>
          {Array.from({ length: SKELETON_ROWS }, (_, index) => (
            <Skeleton
              borderRadius={SKELETON_RADIUS}
              height={SKELETON_ROW_HEIGHT}
              key={index}
              width="100%"
            />
          ))}
        </View>
      );
    }
    if (list.error) {
      return (
        <EmptyState
          action={
            <Button onPress={list.refetch} size="$sm" variant="secondary">
              {i18n.t('sales_portal.error.retry')}
            </Button>
          }
          title={i18n.t('sales_portal.superintelligence.conversations_error')}
        />
      );
    }
    if (ids.length === 0 || rows.length === 0) {
      const emptyKey =
        ids.length > 0
          ? 'sales_portal.superintelligence.conversations_search_empty'
          : showArchived
            ? 'sales_portal.superintelligence.conversations_archived_empty'
            : 'sales_portal.superintelligence.conversations_empty';
      return (
        <Typography color="$textSecondary" paddingHorizontal="$8" size="$sm">
          {i18n.t(emptyKey)}
        </Typography>
      );
    }
    return GROUP_ORDER.map((group) => {
      const grouped = rows.filter(
        (row) =>
          threadDateGroup(row.updatedAt ?? new Date().toISOString()) === group,
      );
      if (grouped.length === 0) {
        return null;
      }
      return (
        <View key={group} style={styles.group}>
          <Typography
            color="$textTertiary"
            paddingHorizontal="$8"
            paddingTop="$4"
            size="$xs"
          >
            {i18n.t(`sales_portal.superintelligence.group_${group}`)}
          </Typography>
          <List>
            {grouped.map((row) => (
              <ThreadRowItem
                archived={showArchived}
                key={row.id}
                onPress={() => select(row.id)}
                row={row}
                selected={row.id === mainThreadId}
              />
            ))}
          </List>
        </View>
      );
    });
  };

  return (
    <View style={styles.container}>
      <SearchInput
        clearAriaLabel={i18n.t('clear')}
        onChangeText={setQuery}
        onClear={() => setQuery('')}
        placeholder={i18n.t(
          'sales_portal.superintelligence.search_conversations',
        )}
        value={query}
      />
      {showArchived ? (
        <List>
          <List.Item
            onPress={() => setShowArchived(false)}
            pressable
            size="$sm"
          >
            <List.Item.Leading>
              <Icons.ChevronLeft color="$iconSecondary" size="$16" />
            </List.Item.Leading>
            <List.Item.Content>
              <List.Item.Title>
                {i18n.t('sales_portal.superintelligence.archived')}
              </List.Item.Title>
            </List.Item.Content>
          </List.Item>
        </List>
      ) : null}
      {renderBody()}
      {list.hasMore ? (
        <Button
          loading={list.isLoadingMore}
          onPress={list.loadMore}
          size="$sm"
          variant="tertiary"
        >
          {i18n.t('sales_portal.superintelligence.load_more')}
        </Button>
      ) : null}
      {showArchived ? null : (
        <List>
          <List.Item onPress={() => setShowArchived(true)} pressable size="$sm">
            <List.Item.Content>
              <List.Item.Title color="$textSecondary">
                {i18n.t('sales_portal.superintelligence.archived')}
              </List.Item.Title>
            </List.Item.Content>
            <List.Item.Trailing>
              <Icons.ChevronRight color="$iconSecondary" size="$16" />
            </List.Item.Trailing>
          </List.Item>
        </List>
      )}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    gap: theme.sk.spacing.spaceSmall,
  },
  group: {
    gap: theme.sk.spacing.spaceXsmall,
  },
  skeleton: {
    gap: theme.sk.spacing.spaceXsmall,
  },
}));
